import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  MenuItem,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ConfirmAllotDialog, { isAlreadyAllotted } from "./ConfirmAllotDialog";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  FULL: { label: "Ready", type: "success" },
  PARTIAL: { label: "Partially available", type: "warning" },
  NOT_POSSIBLE: { label: "Material short", type: "error" },
  NO_BOM: { label: "No BOM", type: "default" },
  NO_DEMAND: { label: "No demand", type: "default" },
  ALLOTTED: { label: "Allotted", type: "success" },
  ALLOTTED_SHORT: { label: "Allotted – material short", type: "error" },
};

// Fully allotted line whose allotted quantity is not covered by stock and POs.
const isAllottedShort = (row) => row?.status === "ALLOTTED_SHORT";

const authHeader = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  return { headers: { Authorization: `Bearer ${user?.accessToken}` } };
};

const getError = (err, fallback) =>
  err?.response?.data?.Exception || err?.response?.data?.message || fallback;

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const getMonths = (startMonth) => {
  if (!startMonth) return [];
  const [year, month] = startMonth.split("-").map(Number);
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(year, month - 1 + index, 1);
    return {
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}`,
      label: date.toLocaleString("en-IN", {
        month: "short",
        year: "numeric",
      }),
    };
  });
};

const getPoDeliveryDate = (material) => {
  return material?.poDeliveryDate || null;
};

// poCount only counts POs that still have quantity to deliver.
const hasPendingPo = (material) => {
  return Number(material?.poCount || 0) > 0;
};

const isMaterialShort = (material) => Number(material?.shortfall || 0) > 0;

const hasLeadTime = (material) =>
  material?.leadTimeDays !== null && material?.leadTimeDays !== undefined;
const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
const getSupplyLabel = (material) => {
  const poExists = hasPendingPo(material);
  const deliveryDate = getPoDeliveryDate(material);
  if (poExists) {
    const poCount = Number(material?.poCount || 0);
    const totalIncoming = Number(material?.totalPendingPoQty || 0);
    if (deliveryDate) {
      return {
        title: poCount > 1 ? `${poCount} Open POs` : "PO delivery",
        value:
          poCount > 1
            ? `${formatNumber(totalIncoming)} incoming • Next ${formatDate(
                deliveryDate,
              )}`
            : `${formatDate(deliveryDate)} • ${formatNumber(
                material?.poPendingQty || 0,
              )} pending`,
        tone: "po",
      };
    }
    return {
      title: poCount > 1 ? `${poCount} Open POs` : "PO exists",
      value:
        poCount > 1
          ? `${formatNumber(totalIncoming)} incoming • Date unavailable`
          : "Delivery date unavailable",
      tone: "warning",
    };
  }
  // No PO quantity pending: lead time only matters while the material is short.
  if (!isMaterialShort(material)) {
    return {
      title: "Available",
      value: "No order needed",
      tone: "muted",
    };
  }
  const deliveredNote =
    Number(material?.deliveredPoCount || 0) > 0 ? "PO fully delivered • " : "";
  if (hasLeadTime(material)) {
    return {
      title: "Lead time",
      value: `${deliveredNote}${material.leadTimeDays} days`,
      tone: "warning",
    };
  }
  return {
    title: deliveredNote ? "PO delivered" : "No PO",
    value: `${deliveredNote}Lead time unavailable`,
    tone: "muted",
  };
};

// Quantity that could not be produced = remaining requirement - can produce
const getProductionGap = (row) =>
  Math.max(Number(row?.remainingQty || 0) - Number(row?.producibleQty || 0), 0);

// Materials are only a problem when there is a real production gap,
// or when the allotted quantity is not covered.
const getShortageMaterials = (row) =>
  getProductionGap(row) > 0 || isAllottedShort(row)
    ? (row?.materials || []).filter(
        (material) => Number(material.shortfall || 0) > 0,
      )
    : [];

const getStatus = (status) =>
  STATUS[status] || {
    label: status || "Unknown",
    type: "default",
  };
const getAllocationKey = (row) =>
  row?.salesItemId ||
  `${row?.itemCode || ""}-${row?.customer || ""}-${row?.priority || ""}`;
const getAllocationNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};
const ProductionPlan = () => {
  const [latestReport, setLatestReport] = useState(null);
  const [month, setMonth] = useState("");
  const [plan, setPlan] = useState(null);
  const [loadingReport, setLoadingReport] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [error, setError] = useState("");
  const [shortageRow, setShortageRow] = useState(null);
  const [allocationPreview, setAllocationPreview] = useState(null);
  const [allocationQty, setAllocationQty] = useState({});
  const [allocationSaving, setAllocationSaving] = useState(false);
  const [mergeConfirm, setMergeConfirm] = useState(null);
  const months = getMonths(latestReport?.startMonth);
  const rows = plan?.rows || [];
  const summary = useMemo(() => {
    let ready = 0;
    let procurementNeeded = 0;
    let plannedQty = 0;
    rows.forEach((row) => {
      plannedQty += Number(row.planQty || 0);
      if (row.status === "FULL" || row.status === "ALLOTTED") ready += 1;
      if (getShortageMaterials(row).length) procurementNeeded += 1;
    });
    return {
      requirements: rows.length,
      plannedQty,
      ready,
      procurementNeeded,
    };
  }, [rows]);
  const fetchPlan = async (reportId, planningMonth) => {
    if (!reportId || !planningMonth) return;
    try {
      setLoadingPlan(true);
      setError("");
      setShortageRow(null);
      const res = await axios.get(
        `${API}/sales-report/${reportId}/plan?month=${planningMonth}`,
        authHeader(),
      );
      setPlan(res.data);
      // Allocation is additive. If the allocation endpoint fails, the
      // existing production planning screen continues to work normally.
      try {
        const allocationRes = await axios.get(
          `${API}/production-allocation/preview?reportId=${reportId}&month=${planningMonth}`,
          authHeader(),
        );
        const preview = allocationRes.data || null;
        setAllocationPreview(preview);
        // The planner enters production quantities; only a saved draft pre-fills them.
        const nextAllocation = {};
        (preview?.lines || []).forEach((line) => {
          if (!line.salesItemId) return;
          nextAllocation[line.salesItemId] = getAllocationNumber(
            line.reservedQty,
          );
        });
        setAllocationQty(nextAllocation);
      } catch (allocationErr) {
        setAllocationPreview(null);
        setAllocationQty({});
      }
    } catch (err) {
      setPlan(null);
      setAllocationPreview(null);
      setAllocationQty({});
      setError(getError(err, "Unable to calculate production plan."));
    } finally {
      setLoadingPlan(false);
    }
  };
  const fetchLatestReport = async (reloadPlan = true) => {
    try {
      setLoadingReport(true);
      setError("");
      const res = await axios.get(`${API}/sales-report`, authHeader());
      const data = res.data || [];
      const latest = data.length ? data[0] : null;
      setLatestReport(latest);
      if (!latest) {
        setMonth("");
        setPlan(null);
        return;
      }
      const reportMonths = getMonths(latest.startMonth);
      const validMonth = reportMonths.some((item) => item.value === month)
        ? month
        : reportMonths[0]?.value || "";
      setMonth(validMonth);
      if (reloadPlan && validMonth) {
        await fetchPlan(latest.reportId, validMonth);
      }
    } catch (err) {
      setLatestReport(null);
      setMonth("");
      setPlan(null);
      setError(getError(err, "Unable to load the latest sales report."));
    } finally {
      setLoadingReport(false);
    }
  };
  useEffect(() => {
    fetchLatestReport(false);
  }, []);
  useEffect(() => {
    if (!latestReport?.startMonth) {
      setMonth("");
      return;
    }
    const reportMonths = getMonths(latestReport.startMonth);
    setMonth((current) =>
      reportMonths.some((item) => item.value === current)
        ? current
        : reportMonths[0]?.value || "",
    );
  }, [latestReport?.reportId, latestReport?.startMonth]);
  useEffect(() => {
    if (latestReport?.reportId && month) {
      fetchPlan(latestReport.reportId, month);
    } else {
      setPlan(null);
      setAllocationPreview(null);
      setAllocationQty({});
    }
  }, [latestReport?.reportId, month]);
  const handleAllocationChange = (row, rawValue) => {
    // No upper limit: the planner decides the month's production quantity.
    setAllocationQty((current) => ({
      ...current,
      [getAllocationKey(row)]: getAllocationNumber(rawValue),
    }));
  };
  const buildAllocationRequest = () => ({
    reportId: latestReport?.reportId,
    month,
    lines: rows
      .filter((row) => row.salesItemId)
      .map((row) => ({
        salesItemId: row.salesItemId,
        allocatedQty: getAllocationNumber(allocationQty[getAllocationKey(row)]),
      })),
  });
  const handleReserve = async () => {
    if (!latestReport?.reportId || !month || !rows.length) return;
    try {
      setAllocationSaving(true);
      setError("");
      await axios.post(
        `${API}/production-allocation/reserve`,
        buildAllocationRequest(),
        authHeader(),
      );
      await fetchPlan(latestReport.reportId, month);
    } catch (err) {
      setError(getError(err, "Unable to reserve the allocation."));
    } finally {
      setAllocationSaving(false);
    }
  };
  const handleAllot = async () => {
    if (!latestReport?.reportId || !month || !rows.length) return;
    let allocationId = null;
    try {
      setAllocationSaving(true);
      setError("");
      // Save the edited quantities as the draft first, then finalize that draft.
      const reserveRes = await axios.post(
        `${API}/production-allocation/reserve`,
        buildAllocationRequest(),
        authHeader(),
      );
      allocationId =
        reserveRes.data?.allocationId || allocationPreview?.allocationId;
      if (!allocationId) {
        throw new Error("Allocation draft could not be created.");
      }
      await axios.post(
        `${API}/production-allocation/${allocationId}/allot`,
        {},
        authHeader(),
      );
      await fetchPlan(latestReport.reportId, month);
    } catch (err) {
      if (isAlreadyAllotted(err)) {
        // The draft stays saved; the planner decides whether to add it to the allotment.
        setMergeConfirm({
          allocationId,
          message: err.response.data.Exception,
        });
        await fetchPlan(latestReport.reportId, month);
      } else {
        setError(getError(err, "Unable to allot the production allocation."));
      }
    } finally {
      setAllocationSaving(false);
    }
  };
  const handleConfirmMerge = async () => {
    if (!mergeConfirm?.allocationId) return;
    try {
      setAllocationSaving(true);
      setError("");
      await axios.post(
        `${API}/production-allocation/${mergeConfirm.allocationId}/allot?confirmMerge=true`,
        {},
        authHeader(),
      );
      setMergeConfirm(null);
      await fetchPlan(latestReport.reportId, month);
    } catch (err) {
      setMergeConfirm(null);
      setError(getError(err, "Unable to add to the existing allotment."));
    } finally {
      setAllocationSaving(false);
    }
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        px: { xs: 1.5, sm: 2.5, md: 3 },
        py: { xs: 2, md: 3 },
      }}
    >
      <PageHeader
        onRefresh={() => fetchLatestReport(true)}
        loading={loadingReport || loadingPlan}
      />
      <ConfirmAllotDialog
        open={Boolean(mergeConfirm)}
        message={mergeConfirm?.message}
        saving={allocationSaving}
        onCancel={() => setMergeConfirm(null)}
        onConfirm={handleConfirmMerge}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {loadingReport && !latestReport ? (
        <EmptyState loading message="Loading the latest sales report..." />
      ) : !latestReport ? (
        <EmptyState
          message="No sales report available"
          description="Upload a sales report from Sales Reports to start production planning."
        />
      ) : (
        <>
          <PlanningContext
            report={latestReport}
            month={month}
            months={months}
            onMonthChange={setMonth}
          />
          <PlannerSummary summary={summary} />
          <ProductionRequirementTable
            plan={plan}
            rows={rows}
            loading={loadingPlan}
            month={month}
            onViewShortage={setShortageRow}
            allocationPreview={allocationPreview}
            allocationQty={allocationQty}
            onAllocationChange={handleAllocationChange}
            onReserve={handleReserve}
            onAllot={handleAllot}
            allocationSaving={allocationSaving}
          />
        </>
      )}
      <ShortageDialog row={shortageRow} onClose={() => setShortageRow(null)} />
    </Box>
  );
};
const PageHeader = ({ onRefresh, loading }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: { xs: "flex-start", md: "center" },
      gap: 2,
      mb: 2.5,
      flexDirection: { xs: "column", md: "row" },
    }}
  >
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography
          sx={{
            fontSize: { xs: 24, md: 28 },
            fontWeight: 700,
            color: "#172033",
          }}
        >
          Production Plan
        </Typography>
        <Chip
          label="Procurement Planner"
          size="small"
          sx={{
            height: 23,
            backgroundColor: "#eef4ff",
            color: "#175cd3",
            fontWeight: 600,
            fontSize: 10,
          }}
        />
      </Stack>
      <Typography
        sx={{
          mt: 0.5,
          fontSize: 13,
          color: "#667085",
        }}
      >
        Plan production from current demand and act on material shortages.
      </Typography>
    </Box>
    <Button
      variant="outlined"
      startIcon={loading ? <CircularProgress size={15} /> : <RefreshIcon />}
      onClick={onRefresh}
      disabled={loading}
      sx={{
        height: 38,
        borderRadius: 1.5,
        px: 1.75,
        textTransform: "none",
        fontWeight: 600,
        borderColor: "#d0d5dd",
        color: "#344054",
        backgroundColor: "#fff",
        "&:hover": {
          backgroundColor: "#f9fafb",
        },
      }}
    >
      Refresh
    </Button>
  </Box>
);
const PlanningContext = ({ report, month, months, onMonthChange }) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #e4e7ec",
      borderRadius: 2,
      backgroundColor: "#fff",
      mb: 2,
    }}
  >
    <Box
      sx={{
        p: { xs: 1.75, md: 2 },
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "stretch", md: "center" },
        gap: 2,
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "#667085",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Current demand source
          </Typography>
          <Chip
            label="Latest report"
            size="small"
            sx={{
              height: 21,
              fontSize: 10,
              fontWeight: 600,
              backgroundColor: "#ecfdf3",
              color: "#027a48",
            }}
          />
        </Stack>
        <Typography
          sx={{
            mt: 0.45,
            fontSize: 14,
            fontWeight: 600,
            color: "#344054",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {report.fileName || "Latest uploaded sales report"}
        </Typography>
        <Typography
          sx={{
            mt: 0.3,
            fontSize: 12,
            color: "#98a2b3",
          }}
        >
          The planner automatically uses the newest uploaded report.
        </Typography>
      </Box>
      <TextField
        select
        size="small"
        label="Planning month"
        value={month}
        onChange={(e) => onMonthChange(e.target.value)}
        sx={{
          width: { xs: "100%", md: 190 },
          flexShrink: 0,
        }}
      >
        {months.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  </Paper>
);
const PlannerSummary = ({ summary }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr 1fr",
        md: "repeat(4, 1fr)",
      },
      gap: 1.5,
      mb: 2,
    }}
  >
    <Metric
      label="Requirements"
      value={summary.requirements}
      helper="production lines"
    />
    <Metric
      label="Planned Quantity"
      value={formatNumber(summary.plannedQty)}
      helper="units"
    />
    <Metric
      label="Ready"
      value={summary.ready}
      helper="without shortage"
      success
    />
    <Metric
      label="Procurement Needed"
      value={summary.procurementNeeded}
      helper="requirements with shortage"
      warning={summary.procurementNeeded > 0}
    />
  </Box>
);
const Metric = ({ label, value, helper, success, warning }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.6,
      border: "1px solid #e4e7ec",
      borderRadius: 2,
      backgroundColor: "#fff",
    }}
  >
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        color: "#667085",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.4,
        fontSize: 20,
        fontWeight: 700,
        color: warning ? "#b42318" : success ? "#027a48" : "#172033",
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ mt: 0.1, fontSize: 11, color: "#98a2b3" }}>
      {helper}
    </Typography>
  </Paper>
);
const ProductionRequirementTable = ({
  plan,
  rows,
  loading,
  month,
  onViewShortage,
  allocationPreview,
  allocationQty,
  onAllocationChange,
  onReserve,
  onAllot,
  allocationSaving,
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #e4e7ec",
      borderRadius: 2,
      overflow: "hidden",
      backgroundColor: "#fff",
    }}
  >
    <Box
      sx={{
        p: { xs: 1.75, md: 2.25 },
        borderBottom: "1px solid #eaecf0",
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center" },
        gap: 2,
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: "#172033",
          }}
        >
          Production Requirements
        </Typography>
        <Typography
          sx={{
            mt: 0.35,
            fontSize: 12,
            color: "#667085",
          }}
        >
          Enter the production quantity for each line. Reserve saves a draft;
          Allot finalizes it and the buyer sees the BOM material requirement
          for that quantity.
        </Typography>
      </Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ flexShrink: 0 }}
      >
        <Chip
          label={plan?.label || month}
          size="small"
          sx={{
            backgroundColor: "#f2f4f7",
            color: "#475467",
            fontWeight: 600,
            fontSize: 11,
          }}
        />
        {allocationPreview?.hasDraft && (
          <Chip
            label="Draft reserved"
            size="small"
            sx={{
              backgroundColor: "#fffaeb",
              color: "#b54708",
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={onReserve}
          disabled={loading || allocationSaving || !allocationPreview}
          sx={{
            height: 32,
            textTransform: "none",
            fontSize: 11,
            fontWeight: 600,
            borderRadius: 1.25,
            borderColor: "#98a2b3",
            color: "#344054",
            backgroundColor: "#fff",
            whiteSpace: "nowrap",
          }}
        >
          {allocationSaving ? "Saving..." : "Reserve"}
        </Button>
        <Button
          size="small"
          className="btn-contained"
          onClick={onAllot}
          disabled={loading || allocationSaving || !allocationPreview}
          sx={{
            height: 32,
            textTransform: "none",
            fontSize: 11,
            fontWeight: 600,
            borderRadius: 1.25,
            boxShadow: "none",
            whiteSpace: "nowrap",
            width: "auto",
          }}
        >
          {allocationSaving ? "Processing..." : "Allot"}
        </Button>
      </Stack>
    </Box>
    {loading ? (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress size={28} />
        <Typography
          sx={{
            mt: 1.5,
            fontSize: 13,
            color: "#667085",
          }}
        >
          Calculating production requirements...
        </Typography>
      </Box>
    ) : !rows.length ? (
      <Box sx={{ py: 7, textAlign: "center" }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: "#475467",
          }}
        >
          No production requirements found
        </Typography>
        <Typography
          sx={{
            mt: 0.5,
            fontSize: 13,
            color: "#98a2b3",
          }}
        >
          No demand is available for this planning month.
        </Typography>
      </Box>
    ) : (
      <TableContainer sx={{ maxHeight: "calc(100vh - 330px)" }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1380 }}>
          <TableHead>
            <TableRow>
              <HeaderCell>Priority</HeaderCell>
              <HeaderCell>Customer</HeaderCell>
              <HeaderCell>Item</HeaderCell>
              <HeaderCell>SKU</HeaderCell>
              <HeaderCell align="right">Requirement</HeaderCell>
              <HeaderCell align="right">Allotted</HeaderCell>
              <HeaderCell align="right">Can Produce</HeaderCell>
              <HeaderCell align="right">Production Qty</HeaderCell>
              <HeaderCell align="center">Shortage</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell align="center">Action</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <RequirementRow
                key={`${row.itemCode}-${index}`}
                row={row}
                index={index}
                onViewShortage={() => onViewShortage(row)}
                allocationQty={allocationQty}
                onAllocationChange={onAllocationChange}
                editable={Boolean(allocationPreview) && !allocationSaving}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
    {!loading && allocationPreview && rows.length > 0 && (
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderTop: "1px solid #eaecf0",
          backgroundColor: "#fcfcfd",
        }}
      >
        <Typography sx={{ fontSize: 11, color: "#667085" }}>
          Production quantity is not limited by Can Produce or the requirement.
          Quantities above Can Produce show up as material shortages for the
          buyer. Reserve does not consume stock; Allot is final.
        </Typography>
      </Box>
    )}
  </Paper>
);
const RequirementRow = ({
  row,
  index,
  onViewShortage,
  allocationQty,
  onAllocationChange,
  editable,
}) => {
  const shortageMaterials = getShortageMaterials(row);
  const hasShortage = shortageMaterials.length > 0;
  const status = getStatus(row.status);
  const productionQty = getAllocationNumber(
    allocationQty?.[getAllocationKey(row)],
  );
  const aboveCanProduce = productionQty > Number(row.producibleQty || 0);
  return (
    <TableRow
      hover
      sx={{
        backgroundColor: hasShortage ? "#fffdfd" : "#fff",
        "&:last-child td": { borderBottom: 0 },
      }}
    >
      <TableCell sx={{ width: 70 }}>
        {/* A blank priority is planned last and shown as "-", not as a row number */}
        <PriorityBadge priority={row.priority} />
      </TableCell>
      <TableCell sx={{ maxWidth: 180 }}>
        <Cell value={row.customer || "-"} strong />
      </TableCell>
      <TableCell sx={{ maxWidth: 170 }}>
        <Cell value={row.itemCode || "-"} />
      </TableCell>
      <TableCell sx={{ maxWidth: 150 }}>
        <Cell value={row.sku || "-"} mono />
      </TableCell>
      <TableCell align="right">
        <Cell value={formatNumber(row.planQty)} strong />
      </TableCell>
      <TableCell align="right">
        <Cell
          value={formatNumber(row.allottedQty)}
          strong
          sx={{
            color: Number(row.allottedQty || 0) > 0 ? "#175cd3" : "#98a2b3",
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Cell
          value={formatNumber(row.producibleQty)}
          strong
          sx={{
            color:
              Number(row.producibleQty || 0) < Number(row.planQty || 0)
                ? "#b54708"
                : "#027a48",
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ width: 120 }}>
        <TextField
          type="number"
          size="small"
          value={productionQty || ""}
          placeholder="0"
          disabled={!editable || !row.salesItemId}
          onChange={(e) => onAllocationChange(row, e.target.value)}
          inputProps={{ min: 0, "aria-label": "Production quantity" }}
          title={
            aboveCanProduce
              ? "Above Can Produce — the buyer will see a material shortage"
              : undefined
          }
          sx={{
            width: 110,
            "& input": {
              textAlign: "right",
              fontSize: 12,
              fontWeight: 600,
              py: 0.75,
              color: aboveCanProduce ? "#b54708" : "#344054",
            },
          }}
        />
      </TableCell>
      <TableCell align="center">
        {hasShortage ? (
          <Chip
            size="small"
            label={`${shortageMaterials.length} material${
              shortageMaterials.length > 1 ? "s" : ""
            }`}
            sx={{
              height: 25,
              backgroundColor: "#fef3f2",
              color: "#b42318",
              fontSize: 10,
              fontWeight: 700,
            }}
          />
        ) : (
          <Typography sx={{ color: "#98a2b3", fontSize: 13 }}>—</Typography>
        )}
      </TableCell>
      <TableCell>
        <StatusChip status={status} />
      </TableCell>
      <TableCell align="center">
        {hasShortage ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ShoppingCartOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={onViewShortage}
            sx={{
              textTransform: "none",
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 1.25,
              borderColor: "#fda29b",
              color: "#b42318",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "#fff5f4",
              },
            }}
          >
            View requirement
          </Button>
        ) : row.status === "FULL" || row.status === "ALLOTTED" ? (
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="center"
            alignItems="center"
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 15, color: "#12b76a" }} />
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: "#027a48",
              }}
            >
              {row.status === "ALLOTTED" ? "Covered" : "Ready"}
            </Typography>
          </Stack>
        ) : (
          <Typography sx={{ color: "#98a2b3", fontSize: 13 }}>—</Typography>
        )}
      </TableCell>
    </TableRow>
  );
};
const HeaderCell = ({ children, align = "left" }) => (
  <TableCell
    align={align}
    sx={{
      backgroundColor: "#f8fafc",
      color: "#667085",
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.03em",
      whiteSpace: "nowrap",
      borderBottom: "1px solid #eaecf0",
    }}
  >
    {children}
  </TableCell>
);
const Cell = ({ value, strong, mono, sx = {} }) => (
  <Typography
    title={value}
    sx={{
      fontSize: 12,
      fontWeight: strong ? 600 : 400,
      color: "#344054",
      fontFamily: mono ? "IBM Plex Mono, monospace" : "inherit",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      ...sx,
    }}
  >
    {value}
  </Typography>
);
const PriorityBadge = ({ priority }) => {
  const topPriority = Number(priority) === 1;
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: topPriority ? "#fef3f2" : "#f2f4f7",
        color: topPriority ? "#b42318" : "#475467",
        fontSize: 12,
        fontWeight: 700,
        mx: "auto",
      }}
    >
      {priority || "-"}
    </Box>
  );
};
const StatusChip = ({ status }) => {
  if (status.type === "success") {
    return (
      <Chip
        size="small"
        icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
        label={status.label}
        sx={{
          height: 25,
          backgroundColor: "#ecfdf3",
          color: "#027a48",
          fontSize: 10,
          fontWeight: 600,
          "& .MuiChip-icon": {
            color: "#12b76a",
          },
        }}
      />
    );
  }
  if (status.type === "warning") {
    return (
      <Chip
        size="small"
        label={status.label}
        sx={{
          height: 25,
          backgroundColor: "#fffaeb",
          color: "#b54708",
          fontSize: 10,
          fontWeight: 600,
        }}
      />
    );
  }
  if (status.type === "error") {
    return (
      <Chip
        size="small"
        icon={<WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />}
        label={status.label}
        sx={{
          height: 25,
          backgroundColor: "#fef3f2",
          color: "#b42318",
          fontSize: 10,
          fontWeight: 600,
          "& .MuiChip-icon": {
            color: "#f04438",
          },
        }}
      />
    );
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      label={status.label}
      sx={{
        height: 25,
        fontSize: 10,
        fontWeight: 600,
        color: "#475467",
      }}
    />
  );
};
const EmptyState = ({ loading, message, description }) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #e4e7ec",
      borderRadius: 2,
      backgroundColor: "#fff",
    }}
  >
    <Box
      sx={{
        py: 7,
        textAlign: "center",
      }}
    >
      {loading ? (
        <CircularProgress size={28} />
      ) : (
        <Inventory2OutlinedIcon
          sx={{
            fontSize: 38,
            color: "#98a2b3",
          }}
        />
      )}
      <Typography
        sx={{
          mt: 1.25,
          fontSize: 14,
          fontWeight: 600,
          color: "#475467",
        }}
      >
        {message}
      </Typography>
      {description && (
        <Typography
          sx={{
            mt: 0.5,
            fontSize: 13,
            color: "#98a2b3",
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  </Paper>
);
const ShortageDialog = ({ row, onClose }) => {
  const allotted = isAllottedShort(row);
  // Show every BOM material needed for the gap or the allotment (not only the short ones)
  const materials =
    getProductionGap(row) > 0 || allotted ? row?.materials || [] : [];
  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid #eaecf0",
        }}
      >
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: "#172033",
          }}
        >
          Procurement Requirement
        </Typography>
        {row && (
          <Typography
            sx={{
              mt: 0.4,
              fontSize: 13,
              color: "#667085",
            }}
          >
            {row.customer || "Customer"} • {row.itemCode || "Item"}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {row && (
          <>
            <Box
              sx={{
                px: 3,
                py: 2,
                display: "flex",
                gap: 1.25,
                alignItems: "flex-start",
                backgroundColor: "#fff8f7",
                borderBottom: "1px solid #fee4e2",
              }}
            >
              <WarningAmberOutlinedIcon
                sx={{
                  color: "#d92d20",
                  fontSize: 22,
                  mt: 0.1,
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#912018",
                  }}
                >
                  Material shortage detected
                </Typography>
                <Typography
                  sx={{
                    mt: 0.3,
                    fontSize: 12,
                    color: "#b42318",
                  }}
                >
                  {allotted
                    ? "The allotted quantity is not covered by stock and open POs. Raw materials are listed below."
                    : "The production requirement cannot be fully completed with current material availability."}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ p: 3, pb: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "repeat(3, 1fr)",
                  },
                  gap: 1.5,
                }}
              >
                {allotted ? (
                  <>
                    <DialogMetric
                      label="Requirement"
                      value={formatNumber(row.planQty)}
                    />
                    <DialogMetric
                      label="Allotted"
                      value={formatNumber(row.allottedQty)}
                    />
                    <DialogMetric
                      label="Short Materials"
                      value={getShortageMaterials(row).length}
                      danger
                    />
                  </>
                ) : (
                  <>
                    <DialogMetric
                      label="Remaining Requirement"
                      value={formatNumber(row.remainingQty)}
                    />
                    <DialogMetric
                      label="Can Produce"
                      value={formatNumber(row.producibleQty)}
                    />
                    <DialogMetric
                      label="Production Gap"
                      value={formatNumber(getProductionGap(row))}
                      danger
                    />
                  </>
                )}
              </Box>
            </Box>
            <Divider />
            <Box sx={{ p: 3 }}>
              <Typography
                sx={{
                  mb: 1.25,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#344054",
                }}
              >
                Materials to Procure
              </Typography>
              <TableContainer
                sx={{
                  border: "1px solid #eaecf0",
                  borderRadius: 1.5,
                }}
              >
                <Table size="small" sx={{ minWidth: 1180 }}>
                  <TableHead>
                    <TableRow>
                      <HeaderCell>Material</HeaderCell>
                      <HeaderCell>Description</HeaderCell>
                      <HeaderCell>UOM</HeaderCell>
                      <HeaderCell align="right">Required</HeaderCell>
                      <HeaderCell align="right">Available</HeaderCell>
                      <HeaderCell align="right">Suggested Order</HeaderCell>
                      <HeaderCell>Supply Availability</HeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materials.map((material, index) => (
                      <TableRow
                        key={`${material.materialNo || material.material}-${index}`}
                        sx={{
                          "&:last-child td": {
                            borderBottom: 0,
                          },
                        }}
                      >
                        <TableCell>
                          <Cell
                            value={
                              material.materialNo || material.material || "-"
                            }
                            mono
                            strong
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Cell value={material.description || "-"} />
                        </TableCell>
                        <TableCell>
                          <Cell value={material.uom || material.unit || "-"} />
                        </TableCell>
                        <TableCell align="right">
                          <Cell
                            value={formatNumber(
                              material.required ||
                                material.requiredQty ||
                                material.requirement,
                            )}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Cell
                            value={formatNumber(
                              material.available ||
                                material.availableQty ||
                                material.stock,
                            )}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Cell
                            value={formatNumber(material.shortfall)}
                            strong
                            sx={{
                              color:
                                Number(material.shortfall || 0) > 0
                                  ? "#b42318"
                                  : "#98a2b3",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <SupplyAvailabilityCell material={material} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert
                severity="info"
                sx={{
                  mt: 1.5,
                  fontSize: 12,
                }}
              >
                Suggested Order Qty currently equals the material shortfall. If
                a PO is not fully delivered, only the PO is shown. If there is
                no PO, or every PO is fully delivered, and the material is still
                short, Parts Master lead time and supplier information are
                shown.
              </Alert>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 1.75,
          borderTop: "1px solid #eaecf0",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "#475467",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
const SupplyAvailabilityCell = ({ material }) => {
  const [open, setOpen] = useState(false);
  const supply = getSupplyLabel(material);
  const poExists = hasPendingPo(material);
  const leadTimeKnown = hasLeadTime(material);
  const hasSupplierDetails =
    Boolean(material?.supplier1) || Boolean(material?.supplier2);
  const hasMoq = material?.moq !== null && material?.moq !== undefined;
  // Pending PO: PO details only. Otherwise sourcing details only when still short.
  const showDetails =
    (poExists &&
      (material?.poNumber ||
        material?.poSupplier ||
        Number(material?.totalPendingPoQty || 0) > 0)) ||
    (!poExists &&
      isMaterialShort(material) &&
      (leadTimeKnown || hasSupplierDetails || hasMoq));
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color:
                supply.tone === "po"
                  ? "#175cd3"
                  : supply.tone === "warning"
                    ? "#b54708"
                    : "#667085",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
            }}
          >
            {supply.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              color: supply.tone === "po" ? "#344054" : "#667085",
              fontWeight: supply.tone === "po" ? 600 : 400,
              whiteSpace: "normal",
              lineHeight: 1.35,
            }}
          >
            {supply.value}
          </Typography>
        </Box>
        {showDetails && (
          <IconButton
            size="small"
            onClick={() => setOpen((value) => !value)}
            sx={{
              p: 0.25,
              flexShrink: 0,
              color: "#667085",
            }}
            aria-label={
              open ? "Hide procurement details" : "Show procurement details"
            }
          >
            {open ? (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        )}
      </Stack>
      {showDetails && (
        <Collapse in={open}>
          <Box
            sx={{
              mt: 0.5,
              p: 1,
              border: "1px solid #eaecf0",
              borderRadius: 1,
              backgroundColor: "#f9fafb",
              minWidth: 230,
            }}
          >
            {poExists ? (
              <>
                {material?.poNumber && (
                  <DetailLine label="PO" value={material.poNumber} mono />
                )}
                {material?.poSupplier && (
                  <DetailLine label="Supplier" value={material.poSupplier} />
                )}
                {material?.poDeliveryDate && (
                  <DetailLine
                    label="Delivery"
                    value={formatDate(material.poDeliveryDate)}
                  />
                )}
                <DetailLine
                  label="PO Pending"
                  value={formatNumber(material?.poPendingQty || 0)}
                />
                <DetailLine
                  label="Total Incoming"
                  value={formatNumber(material?.totalPendingPoQty || 0)}
                />
                <DetailLine label="Open POs" value={material?.poCount ?? 0} />
              </>
            ) : (
              <>
                {leadTimeKnown && (
                  <DetailLine
                    label="Lead Time"
                    value={`${material.leadTimeDays} days`}
                  />
                )}
                {material?.supplier1 && (
                  <DetailLine label="Supplier 1" value={material.supplier1} />
                )}
                {material?.supplier1 && (
                  <DetailLine
                    label="Capacity 1"
                    value={`${formatNumber(
                      material?.supplier1CapacityPerMonth || 0,
                    )} / month`}
                  />
                )}
                {material?.supplier2 && (
                  <DetailLine label="Supplier 2" value={material.supplier2} />
                )}
                {material?.supplier2 && (
                  <DetailLine
                    label="Capacity 2"
                    value={`${formatNumber(
                      material?.supplier2CapacityPerMonth || 0,
                    )} / month`}
                  />
                )}
                {hasMoq && (
                  <DetailLine label="MOQ" value={formatNumber(material.moq)} />
                )}
              </>
            )}
          </Box>
        </Collapse>
      )}
    </Stack>
  );
};
const DetailLine = ({ label, value, mono = false }) => (
  <Stack
    direction="row"
    spacing={1}
    justifyContent="space-between"
    alignItems="flex-start"
    sx={{
      "& + &": {
        mt: 0.65,
      },
    }}
  >
    <Typography
      sx={{
        fontSize: 10,
        color: "#667085",
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 10,
        color: "#344054",
        fontWeight: 600,
        textAlign: "right",
        fontFamily: mono ? "IBM Plex Mono, monospace" : "inherit",
        wordBreak: "break-word",
      }}
    >
      {value || "-"}
    </Typography>
  </Stack>
);
const DialogMetric = ({ label, value, danger }) => (
  <Box
    sx={{
      p: 1.5,
      border: "1px solid #eaecf0",
      borderRadius: 1.5,
      backgroundColor: "#fff",
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        color: "#667085",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.3,
        fontSize: 18,
        fontWeight: 700,
        color: danger ? "#b42318" : "#172033",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default ProductionPlan;